import { useState, useMemo, useCallback, useEffect, useRef, lazy, Suspense } from "react";
import {
  Search,
  ChevronDown,
  X,
  FileText,
  Layers,
  Download,
  Image as ImageIcon,
  Loader2,
  CheckSquare,
  Maximize2,
  Trash2,
} from "lucide-react";
import { problemUrl, qbFetch, skillsUrl } from "./qb-cache";
import { IdBadge } from "./IdBadge";
import { DeleteProblemDialog } from "./DeleteProblemDialog";
import {
  adjustSkillCount,
  adjustTotal,
  bumpGeneration,
  fetchSkillPage,
  findRow,
  PROBLEM_PAGE_SIZE,
  restoreProblem,
  withoutProblem,
  withRow,
  type ProblemTarget,
  type RemovedRow,
  type SkillGenerations,
} from "./problem-list";
import { normalizeImages } from "./media";

const SurveyRenderer = lazy(() => import("./SurveyRenderer"));
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ProblemText } from "./ProblemText";
import { ProblemImages, ImageBadge, type DiagramState } from "./ProblemImages";
import { toBool, type Problem, type Skill } from "./types";
import { sameSteps } from "./survey-config";

// ── Source display mapping ──

const SOURCE_TO_LABEL: Record<string, string> = {
  ixl: "Source 1",
  khan_academy: "Source 2",
  ka: "Source 2",
  thinkverse: "Source 3",
};

const SOURCE_BADGE_COLORS: Record<string, string> = {
  "Source 1": "border-emerald-200 bg-emerald-50 text-emerald-700",
  "Source 2": "border-blue-200 bg-blue-50 text-blue-700",
  "Source 3": "border-violet-200 bg-violet-50 text-violet-700",
};

const DIFFICULTY_PILL: Record<string, string> = {
  easy: "bg-green-100 text-green-700",
  mild: "bg-green-100 text-green-700",
  medium: "bg-amber-100 text-amber-700",
  hard: "bg-red-100 text-red-700",
  spicy: "bg-red-100 text-red-700",
};

function getSourceLabel(source: string): string {
  return SOURCE_TO_LABEL[source?.toLowerCase()] ?? source;
}

// ── Types ──

interface SkillGroup {
  skill_id: string;
  skill_name: string;
  standard_code: string;
  grade: string;
  source: string;
  problems: Problem[];
  problem_count?: number;
}

// ── Grade ordering ──
// Grades arrive as strings, so "K" must not NaN the numeric sort.

function gradeRank(g: string): number {
  if (/^\d+$/.test(g)) return Number(g);
  return g.trim().toUpperCase() === "K" ? -1 : 99;
}

function byGrade(a: string, b: string): number {
  return gradeRank(a) - gradeRank(b) || a.localeCompare(b);
}

function gradeLabel(g: string): string {
  return g.trim().toUpperCase() === "K" ? "Kindergarten" : `Grade ${g}`;
}

function unionSort(
  prev: string[],
  incoming: string[],
  cmp: (a: string, b: string) => number,
): string[] {
  return [...new Set([...prev, ...incoming.filter(Boolean)])].sort(cmp);
}

// ── Cross-tab persistence ──
// Dashboard's key={activeTab} unmounts this on every tab switch, so React state
// cannot survive. Module vars do, and reset on reload (as GenerationEngine does).

let _cachedGrade = "all";
let _cachedSource = "all";
let _cachedStandard = "all";
let _cachedSearch = "";
let _cachedExpanded: string | null = null;
let _cachedListPage = 0;
let _cachedSkillProblems = new Map<string, Problem[]>();
let _cachedSkillTotals = new Map<string, number>();
let _cachedFacets: { grades: string[]; standards: string[] } = {
  grades: [],
  standards: [],
};

const SEARCH_DEBOUNCE_MS = 300;

// ── Stat Card ──

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: React.ElementType;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </span>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="text-3xl font-bold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

// ── Problem Preview ──

// ProblemImages / ImageBadge now live in ./ProblemImages (re-exported above).

const EverTutorBadge = () => (
  <Badge variant="outline" className="rounded-md border-emerald-200 bg-emerald-50 font-normal text-emerald-700">
    EverTutor
  </Badge>
);

function ProblemPreview({ problem, diagrams }: { problem: Problem; diagrams: DiagramState }) {
  if (!problem.surveyjs_config) {
    return (
      <div className="qb-safe space-y-3">
        <ProblemText text={problem.question_text} className="text-base leading-relaxed whitespace-pre-wrap" />
        <ProblemImages images={problem.images} diagrams={diagrams} />
        {problem.answer_choices?.length ? (
          <div className="space-y-1.5">
            {problem.answer_choices.map((o, i) => (
              <div
                key={i}
                className={`qb-choice flex items-center gap-3 rounded-lg px-4 py-2.5 ${
                  o === problem.correct_answer ? "border-2 border-primary bg-primary/5" : "border"
                }`}
              >
                <ProblemText text={o} className="text-sm" inline />
              </div>
            ))}
          </div>
        ) : null}
        {/* qb-choice only when there are choices for it to line up with. On a
            free-response problem this is a standalone value, and the 96px box
            would shrink a vertical number line to an unreadable thumbnail. */}
        <div
          className={`qb-safe ${
            problem.answer_choices?.length ? "qb-choice" : "qb-answer"
          } rounded-lg border bg-muted/30 p-3`}
        >
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Correct Answer</div>
          <ProblemText text={problem.correct_answer} className="text-sm font-bold" inline />
        </div>
      </div>
    );
  }
  // SurveyRenderer places the figure itself — see its comment on why.
  return (
    <Suspense fallback={<div className="py-4 text-center text-sm text-muted-foreground">Loading preview...</div>}>
      <SurveyRenderer config={problem.surveyjs_config} images={problem.images} diagrams={diagrams} />
    </Suspense>
  );
}

// ── Export ──

function doExport(groups: SkillGroup[], fmt: "json" | "md") {
  let content: string;
  let mime: string;
  let ext: string;
  if (fmt === "json") {
    content = JSON.stringify(
      groups.map((g) => ({
        skill_id: g.skill_id,
        skill_name: g.skill_name,
        standard_code: g.standard_code,
        grade: g.grade,
        source: g.source,
        problems: g.problems.map((p) => ({
          id: p.id,
          question_text: p.question_text,
          question_type: p.question_type,
          difficulty_level: p.difficulty_level,
          correct_answer: p.correct_answer,
          answer_choices: p.answer_choices,
        })),
      })),
      null, 2,
    );
    mime = "application/json";
    ext = "json";
  } else {
    content = groups
      .map((g) =>
        `# ${g.skill_name} (${g.standard_code})\n${g.problems.map((p, i) => `${i + 1}. [${p.difficulty_level}] ${p.question_text} -> ${p.correct_answer}`).join("\n")}`,
      )
      .join("\n---\n\n");
    mime = "text/markdown";
    ext = "md";
  }
  const blob = new Blob([content], { type: mime });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `skills_export.${ext}`;
  a.click();
}

// ── Skill status dot ──

function skillDotColor(count: number) {
  if (count === 0) return "bg-red-400";
  if (count < 3) return "bg-amber-400";
  return "bg-emerald-500";
}

// ── Main Component ──

const PAGE_SIZE = 200;

/** The same page can arrive twice: "Load more" disables a render late and StrictMode double-runs the expand effect. */
function uniqueById(problems: Problem[]): Problem[] {
  const byId = new Map<string, Problem>();
  for (const p of problems) {
    if (p?.id) byId.set(p.id, p);
  }
  return [...byId.values()];
}

async function fetchAllSkills(filters: {
  grade: string;
  source: string;
  standard: string;
  search: string;
  onProgress: (loaded: number, total: number, totalProblems: number, totalImages: number | null) => void;
  signal: AbortSignal;
}): Promise<{ skills: Skill[]; total: number; totalProblems: number; totalImages: number | null }> {
  const { grade, source, standard, search, onProgress, signal } = filters;

  const buildParams = (offset: number) => {
    const p = new URLSearchParams();
    if (grade !== "all") p.set("grade", grade);
    if (source !== "all") p.set("source", source);
    if (standard !== "all") p.set("standard_code", standard);
    if (search) p.set("search", search);
    p.set("limit", String(PAGE_SIZE));
    p.set("offset", String(offset));
    return p;
  };

  // First page — gets total, total_problems, total_images
  const url0 = skillsUrl(buildParams(0));
  const firstData = await qbFetch<{ items: Skill[]; total: number; total_problems?: number; total_images?: number }>(url0, { signal, ttl: 60_000 });
  const total = firstData.total || 0;
  const totalProblems = firstData.total_problems ?? 0;
  // null, not 0: the studio backend never implemented `total_images`, so `?? 0`
  // turned a missing stat into a measured one. The card hides instead.
  const totalImages = firstData.total_images ?? null;
  const allSkills: Skill[] = firstData.items || [];

  onProgress(allSkills.length, total, totalProblems, totalImages);

  if (allSkills.length >= total) return { skills: allSkills, total, totalProblems, totalImages };

  // Remaining pages in sequence
  let offset = PAGE_SIZE;
  while (offset < total) {
    const url = skillsUrl(buildParams(offset));
    const data = await qbFetch<{ items: Skill[] }>(url, { signal, ttl: 60_000 });
    const items = data.items || [];
    allSkills.push(...items);
    onProgress(allSkills.length, total, totalProblems, totalImages);
    if (items.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }

  return { skills: allSkills, total, totalProblems, totalImages };
}

export function ExtractionEngine() {
  // Live fetch state
  const [liveSkillProblems, setLiveSkillProblems] =
    useState<Map<string, Problem[]>>(_cachedSkillProblems);
  const [skillTotals, setSkillTotals] = useState<Map<string, number>>(_cachedSkillTotals);
  const [skillErrors, setSkillErrors] = useState<Map<string, string>>(new Map());
  const [liveSkillLoading, setLiveSkillLoading] = useState<string | null>(null);
  const [liveFetch, setLiveFetch] = useState<{
    loading: boolean;
    loaded: number;
    total: number;
    totalProblems: number;
    totalImages: number | null;
    skills: Skill[];
  }>({ loading: true, loaded: 0, total: 0, totalProblems: 0, totalImages: null, skills: [] });

  const [gradeFilter, setGradeFilter] = useState(_cachedGrade);
  const [sourceFilter, setSourceFilter] = useState(_cachedSource);
  const [standardFilter, setStandardFilter] = useState(_cachedStandard);
  const [searchQ, setSearchQ] = useState(_cachedSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(_cachedSearch);

  const [facets, setFacets] = useState(_cachedFacets);

  const [expanded, setExpanded] = useState<string | null>(_cachedExpanded);
  const [preview, setPreview] = useState<Problem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProblemTarget | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<{ id: string; message: string } | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [detailLoad, setDetailLoad] = useState<{ id: string; error: string | null; done: boolean } | null>(null);

  const previewId = preview?.id;
  const previewHasDiagram = useMemo(
    () => normalizeImages(preview?.images).some((r) => r.kind === "diagram"),
    [preview?.images],
  );
  useEffect(() => {
    if (!previewId || !previewHasDiagram) return;
    const controller = new AbortController();
    setDetailLoad({ id: previewId, error: null, done: false });
    qbFetch<Problem>(problemUrl(previewId), { ttl: 60_000, signal: controller.signal })
      .then((detail) => {
        if (controller.signal.aborted) return;
        setPreview((p) => (p?.id === detail.id ? detail : p));
        setDetailLoad({ id: previewId, error: null, done: true });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setDetailLoad({
          id: previewId,
          error: err instanceof Error ? err.message : "The problem detail could not be loaded.",
          done: true,
        });
      });
    return () => controller.abort();
  }, [previewId, previewHasDiagram]);

  // Typing stays instant; only the debounced value drives the fetch.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchQ), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchQ]);

  // Mirror state into the module-level cache so it survives a tab switch.
  useEffect(() => {
    _cachedGrade = gradeFilter;
    _cachedSource = sourceFilter;
    _cachedStandard = standardFilter;
    _cachedSearch = searchQ;
    _cachedExpanded = expanded;
    _cachedSkillProblems = liveSkillProblems;
    _cachedSkillTotals = skillTotals;
    _cachedFacets = facets;
  }, [
    gradeFilter,
    sourceFilter,
    standardFilter,
    searchQ,
    expanded,
    liveSkillProblems,
    skillTotals,
    facets,
  ]);

  // Fetch all skills pages whenever filters change
  const fetchSeq = useRef(0);
  useEffect(() => {
    const seq = ++fetchSeq.current;
    const controller = new AbortController();
    setLiveFetch({ loading: true, loaded: 0, total: 0, totalProblems: 0, totalImages: null, skills: [] });

    // Facets must come from an unfiltered response only — deriving them from a
    // filtered one collapses the dropdown to the selected value.
    const unfiltered =
      gradeFilter === "all" &&
      sourceFilter === "all" &&
      standardFilter === "all" &&
      debouncedSearch === "";

    fetchAllSkills({
      grade: gradeFilter,
      source: sourceFilter,
      standard: standardFilter,
      search: debouncedSearch,
      signal: controller.signal,
      onProgress: (loaded, total, totalProblems, totalImages) => {
        if (seq !== fetchSeq.current) return;
        setLiveFetch((prev) => ({ ...prev, loaded, total, totalProblems, totalImages }));
      },
    })
      .then(({ skills }) => {
        if (seq !== fetchSeq.current) return;
        setLiveFetch((prev) => ({ ...prev, skills, loading: false }));
        if (unfiltered) {
          // Union, never replace, so the list is monotonic and cannot shrink.
          setFacets((prev) => ({
            grades: unionSort(prev.grades, skills.map((s) => String(s.grade)), byGrade),
            standards: unionSort(
              prev.standards,
              skills.map((s) => s.standard_code),
              (a, b) => a.localeCompare(b),
            ),
          }));
        }
      })
      .catch((err) => {
        if (seq !== fetchSeq.current) return;
        if (err.name !== "AbortError") setLiveFetch((prev) => ({ ...prev, skills: [], loading: false }));
      });

    return () => controller.abort();
  }, [gradeFilter, sourceFilter, standardFilter, debouncedSearch]);

  const skillGen = useRef<SkillGenerations>(new Map());
  const removedRow = useRef<RemovedRow | null>(null);

  /** The backend caps `limit` at 200 and returns `total`, so paging is client-side. */
  const loadSkillProblems = useCallback(async (skillId: string, offset: number) => {
    setLiveSkillLoading(skillId);
    setSkillErrors((prev) => {
      const next = new Map(prev);
      next.delete(skillId);
      return next;
    });
    try {
      const data = await fetchSkillPage(skillId, offset, skillGen.current);
      setLiveSkillProblems((prev) => {
        const next = new Map(prev);
        const existing = offset === 0 ? [] : (prev.get(skillId) ?? []);
        next.set(skillId, uniqueById([...existing, ...(data.items ?? [])]));
        return next;
      });
      setSkillTotals((prev) => new Map(prev).set(skillId, data.total ?? 0));
    } catch (err) {
      setSkillErrors((prev) =>
        new Map(prev).set(
          skillId,
          err instanceof Error ? err.message : "Failed to load problems",
        ),
      );
    } finally {
      setLiveSkillLoading(null);
    }
  }, []);

  // Fetch problems when a live skill is expanded
  useEffect(() => {
    if (!expanded) return;
    if (liveSkillProblems.has(expanded)) return;
    void loadSkillProblems(expanded, 0);
  }, [expanded, liveSkillProblems, loadSkillProblems]);

  // Rows before the deleted one are unchanged and Load more pages from the list length, so no refetch.
  const removeDeletedProblem = (target: ProblemTarget) => {
    setPreview((p) => (p?.id === target.id ? null : p));
    bumpGeneration(skillGen.current, target.skillId);
    removedRow.current = findRow(liveSkillProblems, target);
    setLiveSkillProblems((prev) => withoutProblem(prev, target));
    setSkillTotals((prev) => adjustTotal(prev, target.skillId, -1));
    setLiveFetch((prev) => adjustSkillCount(prev, target.skillId, -1));
  };

  // The row goes back where it was, so Load more offsets stay right without a refetch.
  const putBackRestoredProblem = (target: ProblemTarget) => {
    const row = removedRow.current;
    if (row?.problem.id === target.id) setLiveSkillProblems((prev) => withRow(prev, row));
    setSkillTotals((prev) => adjustTotal(prev, target.skillId, 1));
    setLiveFetch((prev) => adjustSkillCount(prev, target.skillId, 1));
  };

  // A preview marked deleted is a row someone else deleted; it never left this list or its counts.
  const restorePreview = async (target: ProblemTarget) => {
    setRestoring(true);
    setRestoreError(null);
    try {
      await restoreProblem(target, () =>
        setPreview((p) => (p?.id === target.id ? { ...p, soft_deleted: false } : p)),
      );
    } catch (err) {
      setRestoreError({
        id: target.id,
        message: err instanceof Error ? err.message : "The problem could not be restored.",
      });
    } finally {
      setRestoring(false);
    }
  };

  // Skill groups derived from live fetch
  const skillGroups = useMemo((): SkillGroup[] => {
    return liveFetch.skills.map((s) => ({
      skill_id: s.id,
      skill_name: s.skill_name,
      standard_code: s.standard_code,
      grade: s.grade,
      source: s.source,
      problems: liveSkillProblems.get(s.id) || [],
      problem_count: s.problem_count,
    }));
  }, [liveFetch.skills, liveSkillProblems]);

  const filtered = skillGroups;

  // Built from unfiltered fetches only, so selecting a value cannot shrink these.
  const allGrades = facets.grades;
  const allStandards = facets.standards;

  const getProblemCount = (g: SkillGroup) => g.problem_count ?? g.problems.length;

  const totalProblems = liveFetch.totalProblems || liveFetch.skills.reduce((a, s) => a + (s.problem_count ?? 0), 0);
  const totalImages = liveFetch.totalImages;

  const [listPage, setListPage] = useState(_cachedListPage);
  const LIST_PAGE_SIZE = 50;

  useEffect(() => { _cachedListPage = listPage; }, [listPage]);

  // Reset list page when filters change
  useEffect(() => { setListPage(0); }, [gradeFilter, sourceFilter, standardFilter, debouncedSearch]); // eslint-disable-line

  const pageCount = Math.ceil(filtered.length / LIST_PAGE_SIZE);
  const pagedSkills = filtered.slice(listPage * LIST_PAGE_SIZE, (listPage + 1) * LIST_PAGE_SIZE);

  const allChecked = filtered.length > 0 && filtered.every((g) => checked.has(g.skill_id));

  const toggleCheck = useCallback((id: string) => {
    setChecked((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setChecked((prev) => {
      const n = new Set(prev);
      const ids = filtered.map((g) => g.skill_id);
      const allIn = ids.every((id) => prev.has(id));
      ids.forEach((id) => (allIn ? n.delete(id) : n.add(id)));
      return n;
    });
  }, [filtered]);

  const selectedGroups = skillGroups.filter((g) => checked.has(g.skill_id));

  const selectCls = (active: boolean) =>
    `cursor-pointer appearance-none rounded-lg border px-3 py-2 text-sm font-medium outline-none transition-all pr-8 ${
      active ? "border-primary bg-primary/5 text-primary" : "border bg-background text-foreground"
    }`;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold">Extraction Engine</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Browse scraped problem sets by skill
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="default" variant="outline" className="gap-1.5">
                <Download className="h-4 w-4" />
                Export
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
                {checked.size > 0 ? `${checked.size} selected` : "None selected"}
              </div>
              <DropdownMenuItem
                disabled={checked.size === 0}
                onClick={() => doExport(selectedGroups, "json")}
              >
                <span className="mr-2 font-mono text-xs">{"{ }"}</span> JSON
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={checked.size === 0}
                onClick={() => doExport(selectedGroups, "md")}
              >
                <FileText className="mr-2 h-3.5 w-3.5" /> Markdown
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Loading state — skeleton + progress bar */}
      {liveFetch.loading && (
        <div className="space-y-4">
          {/* Progress */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {liveFetch.total > 0
                  ? `Loading ${liveFetch.loaded} of ${liveFetch.total} skills…`
                  : "Fetching skills…"}
              </span>
              {liveFetch.total > 0 && (
                <span className="ml-auto text-xs text-muted-foreground">
                  {Math.round((liveFetch.loaded / liveFetch.total) * 100)}%
                </span>
              )}
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: liveFetch.total > 0 ? `${Math.round((liveFetch.loaded / liveFetch.total) * 100)}%` : "10%" }}
              />
            </div>
          </div>

          {/* Skeleton filter bar */}
          <div className="flex items-center gap-2">
            <div className="h-10 flex-1 animate-pulse rounded-lg bg-muted" />
            <div className="h-10 w-32 animate-pulse rounded-lg bg-muted" />
            <div className="h-10 w-36 animate-pulse rounded-lg bg-muted" />
            <div className="h-10 w-28 animate-pulse rounded-lg bg-muted" />
          </div>

          {/* Skeleton stat cards */}
          <div className="grid grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-lg border bg-muted/40" />
            ))}
          </div>

          {/* Skeleton skill rows */}
          <div className="overflow-hidden rounded-lg border">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 border-b px-4 py-3.5 last:border-b-0">
                <div className="h-4 w-4 animate-pulse rounded bg-muted" />
                <div className="h-4 w-4 animate-pulse rounded bg-muted" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 w-48 animate-pulse rounded bg-muted" />
                  <div className="flex gap-1.5">
                    <div className="h-3 w-16 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-16 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-16 animate-pulse rounded bg-muted" />
                  </div>
                </div>
                <div className="h-6 w-8 animate-pulse rounded bg-muted" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      {!liveFetch.loading && (
        <>
          {/* Filter row */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="flex flex-1 items-center gap-2 rounded-lg border bg-background px-3 py-2 shadow-sm">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search skills, standards, problems..."
                className="flex-1 border-none bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
              {searchQ && (
                <button onClick={() => setSearchQ("")} className="cursor-pointer border-none bg-transparent p-0">
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              )}
            </div>

            {/* Sources dropdown */}
            <div className="relative">
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className={selectCls(sourceFilter !== "all")}
              >
                <option value="all">All Sources</option>
                <option value="ixl">Source 1</option>
                <option value="khan_academy">Source 2</option>
                <option value="thinkverse">Source 3</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            </div>

            {/* Standards dropdown */}
            <div className="relative">
              <select
                value={standardFilter}
                onChange={(e) => setStandardFilter(e.target.value)}
                className={selectCls(standardFilter !== "all")}
              >
                <option value="all">All Standards</option>
                {allStandards.map((st) => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            </div>

            {/* Grades dropdown */}
            <div className="relative">
              <select
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                className={selectCls(gradeFilter !== "all")}
              >
                <option value="all">All Grades</option>
                {allGrades.map((g) => (
                  <option key={g} value={g}>{gradeLabel(g)}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            </div>
          </div>

          {/* Stats. "With Images" only renders when the backend reports it, so
              the column count follows the cards rather than leaving a gap. */}
          <div className={`grid gap-3 ${totalImages === null ? "grid-cols-3" : "grid-cols-4"}`}>
            <StatCard label="Skills" value={liveFetch.total} icon={Layers} />
            <StatCard label="Problems" value={totalProblems} icon={FileText} />
            {totalImages !== null && (
              <StatCard label="With Images" value={totalImages} icon={ImageIcon} />
            )}
            <StatCard label="Selected" value={checked.size} icon={CheckSquare} />
          </div>

          {/* Skills list */}
          <Card>
            {/* List header */}
            <div className="flex items-center gap-3 border-b px-4 py-3">
              <Checkbox checked={allChecked} onCheckedChange={toggleAll} />
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {filtered.length} Skills
              </span>
              {checked.size > 0 && (
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs font-semibold text-blue-600">{checked.size} selected</span>
                  <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => doExport(selectedGroups, "json")}>JSON</Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => doExport(selectedGroups, "md")}>MD</Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[11px] text-muted-foreground" onClick={() => setChecked(new Set())}>Clear</Button>
                </div>
              )}
            </div>

            {/* Empty state */}
            {filtered.length === 0 && (
              <div className="py-12 text-center text-muted-foreground">
                <Search className="mx-auto mb-2 h-7 w-7 opacity-40" />
                <div className="text-sm font-medium">No skills match your filters</div>
              </div>
            )}

            {/* Skill rows */}
            {pagedSkills.map((sk) => {
              const isExpanded = expanded === sk.skill_id;
              const isChecked = checked.has(sk.skill_id);
              const count = getProblemCount(sk);
              const sourceLabel = getSourceLabel(sk.source);
              const sourceBadgeCls = SOURCE_BADGE_COLORS[sourceLabel] ?? "border-gray-200 bg-gray-50 text-gray-600";
              return (
                <div key={sk.skill_id} className={`border-b last:border-b-0 ${isChecked ? "bg-blue-50/40" : ""}`}>
                  {/* Skill header row */}
                  <div className="flex items-center gap-3 px-4 py-3.5">
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => toggleCheck(sk.skill_id)}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      className="shrink-0"
                    />
                    <button
                      onClick={() => setExpanded(isExpanded ? null : sk.skill_id)}
                      className="shrink-0 cursor-pointer border-none bg-transparent p-0 transition-transform duration-200"
                      style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0)" }}
                    >
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </button>
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setExpanded(isExpanded ? null : sk.skill_id)}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold">{sk.skill_name}</span>
                        <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${skillDotColor(count)}`} />
                        <IdBadge id={sk.skill_id} noun="skill" label="SKILL" />
                        <Badge variant="outline" className="text-[11px] border-blue-200 bg-blue-50 text-blue-700">
                          {sk.standard_code}
                        </Badge>
                        <Badge variant="outline" className={`text-[11px] ${sourceBadgeCls}`}>
                          {sourceLabel}
                        </Badge>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className={`text-2xl font-bold tabular-nums ${count === 0 ? "text-red-400" : ""}`}>
                        {count}
                      </div>
                      <div className="text-[11px] text-muted-foreground">problems</div>
                    </div>
                  </div>

                  {/* Expanded problems */}
                  {isExpanded && (
                    <div className="border-t bg-muted/20">
                      {liveSkillLoading === sk.skill_id && sk.problems.length === 0 ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                          <span className="ml-2 text-sm text-muted-foreground">Loading problems...</span>
                        </div>
                      ) : skillErrors.get(sk.skill_id) ? (
                        <div className="py-8 text-center text-sm text-red-600">
                          {skillErrors.get(sk.skill_id)} —{" "}
                          <button
                            className="cursor-pointer border-none bg-transparent p-0 underline"
                            onClick={() => void loadSkillProblems(sk.skill_id, 0)}
                          >
                            retry
                          </button>
                        </div>
                      ) : sk.problems.length === 0 ? (
                        <div className="py-10 text-center text-muted-foreground">
                          <div className="text-sm font-medium">No problems for this skill</div>
                        </div>
                      ) : (
                        sk.problems.map((pr, pidx) => {
                          const diffCls = DIFFICULTY_PILL[pr.difficulty_level?.toLowerCase()] ?? "bg-gray-100 text-gray-600";
                          return (
                            <div
                              key={pr.id}
                              className={`flex items-start gap-3 px-4 py-4 pl-16 ${pidx < sk.problems.length - 1 ? "border-b" : ""}`}
                            >
                              {/* Number badge */}
                              <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground">
                                {pidx + 1}
                              </div>

                              {/* Content */}
                              <div className="qb-safe min-w-0 flex-1">
                                <ProblemText
                                  text={pr.question_text}
                                  className="mb-2 block text-sm leading-relaxed"
                                />
                                <div className="mb-2 empty:mb-0">
                                  <ProblemImages images={pr.images} diagrams="deferred" />
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${diffCls}`}>
                                    {pr.difficulty_level}
                                  </span>
                                  <span className="rounded-md border px-2 py-0.5 text-xs text-muted-foreground">
                                    {pr.question_type?.toUpperCase()}
                                  </span>
                                  <ImageBadge images={pr.images} />
                                  {pr.is_evertutor && <EverTutorBadge />}
                                  {toBool(pr.reviewed) && (
                                    <span className="rounded-md border px-2 py-0.5 text-xs text-muted-foreground">
                                      reviewed
                                    </span>
                                  )}
                                  <IdBadge id={pr.id} />
                                  <IdBadge
                                    id={pr.skill_id || sk.skill_id}
                                    noun="skill"
                                    label="SKILL"
                                  />
                                </div>
                              </div>

                              {/* Answer + expand */}
                              <div className="shrink-0 text-right">
                                <div className="text-[11px] text-muted-foreground">Answer</div>
                                {/* The width cap must sit on a block box:
                                    max-width does not apply to a non-replaced
                                    inline element, so putting it on the inline
                                    ProblemText let it grow unbounded and
                                    collapse the question column beside it.

                                    No overflow-hidden and no qb-choice: 941
                                    problems store a figure as the answer (a
                                    120x240 number line here). Cropping it hid
                                    the answer outright, and a uniform box would
                                    shrink it to an unreadable thumbnail — this
                                    is a single value, not a set being compared.
                                    qb-answer scales it to fit instead. */}
                                <div className="qb-safe qb-answer max-w-[160px] text-sm font-semibold text-emerald-600">
                                  <ProblemText text={pr.correct_answer} inline />
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="mt-0.5 h-7 w-7 shrink-0 p-0"
                                onClick={(e) => { e.stopPropagation(); setPreview(pr); }}
                              >
                                <Maximize2 className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="mt-0.5 h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive"
                                aria-label={`Delete ${pr.id}`}
                                title="Delete from the question bank"
                                onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: pr.id, skillId: pr.skill_id || sk.skill_id }); }}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          );
                        })
                      )}
                      {sk.problems.length > 0 &&
                        sk.problems.length < (skillTotals.get(sk.skill_id) ?? 0) && (
                          <div className="flex items-center justify-between border-t px-4 py-3 pl-16 text-xs text-muted-foreground">
                            <span>
                              Showing {sk.problems.length} of {skillTotals.get(sk.skill_id)} problems
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              disabled={liveSkillLoading === sk.skill_id}
                              onClick={() => void loadSkillProblems(sk.skill_id, sk.problems.length)}
                            >
                              {liveSkillLoading === sk.skill_id
                                ? "Loading..."
                                : `Load ${PROBLEM_PAGE_SIZE} more`}
                            </Button>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Pagination controls */}
            {pageCount > 1 && (
              <div className="flex items-center justify-between border-t px-4 py-3">
                <span className="text-xs text-muted-foreground">
                  {listPage * LIST_PAGE_SIZE + 1}–{Math.min((listPage + 1) * LIST_PAGE_SIZE, filtered.length)} of {filtered.length} skills
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 w-7 p-0"
                    disabled={listPage === 0}
                    onClick={() => setListPage(0)}
                  >
                    <ChevronDown className="h-3.5 w-3.5 rotate-90" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs"
                    disabled={listPage === 0}
                    onClick={() => setListPage((p) => p - 1)}
                  >
                    Prev
                  </Button>
                  <span className="px-2 text-xs font-medium">
                    {listPage + 1} / {pageCount}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs"
                    disabled={listPage >= pageCount - 1}
                    onClick={() => setListPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 w-7 p-0"
                    disabled={listPage >= pageCount - 1}
                    onClick={() => setListPage(pageCount - 1)}
                  >
                    <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-h-[90vh] sm:max-w-7xl w-[95vw] overflow-auto">
          <DialogHeader>
            <DialogTitle>Problem Preview</DialogTitle>
            {preview && (
              <div className="mt-0.5 flex flex-wrap items-center gap-2">
                <p className="text-xs text-muted-foreground">
                  {preview.standard_code} · {preview.difficulty_level} · {preview.question_type} · {getSourceLabel(preview.source)}
                </p>
                <IdBadge id={preview.id} />
                {preview.is_evertutor && <EverTutorBadge />}
                {preview.soft_deleted ? (
                  <>
                    <span className="rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs text-red-700">
                      deleted
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={restoring}
                      onClick={() => void restorePreview({ id: preview.id, skillId: preview.skill_id })}
                    >
                      Restore
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1.5 text-xs text-destructive hover:text-destructive"
                    onClick={() => setDeleteTarget({ id: preview.id, skillId: preview.skill_id })}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </Button>
                )}
              </div>
            )}
          </DialogHeader>
          {preview && restoreError?.id === preview.id && (
            <p role="alert" className="text-sm text-destructive">
              Problem could not be restored: {restoreError.message}
            </p>
          )}
          {preview && detailLoad?.id === preview.id && detailLoad.error && (
            <p role="alert" className="text-sm text-destructive">
              Problem detail could not be loaded: {detailLoad.error}
            </p>
          )}
          {preview && (
            <ProblemPreview
              problem={preview}
              diagrams={detailLoad?.id === preview.id && !detailLoad.done ? "loading" : "missing"}
            />
          )}
          {/* Hints alone when the two columns are identical — see sameSteps. */}
          {preview?.explanation_steps &&
            preview.explanation_steps.length > 0 &&
            !sameSteps(preview.explanation_steps, preview.hint_text) && (
            <div className="qb-safe mt-4 space-y-2 rounded-lg border bg-muted/30 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Explanation Steps</div>
              {preview.explanation_steps.map((step, i) => (
                <div key={i} className="flex gap-2 text-sm">
                  <span className="shrink-0 font-semibold text-muted-foreground">{i + 1}.</span>
                  <ProblemText text={step} className="whitespace-pre-wrap" />
                </div>
              ))}
            </div>
          )}
          {preview?.hint_text && preview.hint_text.length > 0 && (
            <div className="qb-safe mt-2 space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-amber-700">Hints</div>
              {preview.hint_text.map((hint, i) => (
                <div key={i} className="flex gap-2 text-sm text-amber-800">
                  <span className="shrink-0 font-semibold">{i + 1}.</span>
                  <ProblemText text={hint} className="whitespace-pre-wrap" />
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DeleteProblemDialog
        key={deleteTarget?.id ?? "none"}
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={removeDeletedProblem}
        onRestored={putBackRestoredProblem}
      />
    </div>
  );
}
